import React, { useState } from "react";
import { Box, Typography, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { PrimaryTooltip } from "../../../Components/StyledComponent";
import { formatPackChipLabel } from "../recordReplacementHelper";

// "Add another pack to this request" — a dashed full-width affordance that opens
// the list of packs on this bus that are not already part of the request, each
// row showing the slot and the serial currently installed in it.
export default function AddPackMenu({ availablePacks = [], onAdd = () => {} }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const isEmpty = !availablePacks.length;

    const handleSelect = (pack) => {
        setAnchorEl(null);
        onAdd(pack);
    };

    const trigger = (
        <Box
            data-testid="add-pack-menu"
            onClick={isEmpty ? undefined : (event) => setAnchorEl(event.currentTarget)}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                border: `1px dashed ${isEmpty ? "#E0E0E0" : "#B9BBF8"}`,
                borderRadius: "12px",
                padding: "16px",
                cursor: isEmpty ? "default" : "pointer",
                color: isEmpty ? "#B0B0B0" : "#4A4AF4",
                backgroundColor: isEmpty ? "#FAFAFA" : "transparent",
                transition: "background-color .15s, border-color .15s",
                ...(!isEmpty && { "&:hover": { backgroundColor: "#F7F7FF", borderColor: "#6D72F6" } }),
            }}
        >
            <AddIcon sx={{ fontSize: 20 }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                Add another pack to this request
            </Typography>
        </Box>
    );

    return (
        <>
            {isEmpty ? (
                <PrimaryTooltip
                    title="Every pack on this bus is already in this request"
                    placement="top"
                >
                    {trigger}
                </PrimaryTooltip>
            ) : (
                trigger
            )}

            <Menu
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                slotProps={{
                    paper: {
                        sx: {
                            // Match the trigger's width so the list reads as an
                            // extension of it rather than a floating popover.
                            width: anchorEl?.offsetWidth,
                            maxHeight: "320px",
                            borderRadius: "12px",
                            boxShadow: "0 8px 24px rgba(2, 6, 24, 0.12)",
                            border: "1px solid #EAEAEA",
                        },
                    },
                }}
                MenuListProps={{ sx: { py: 0 } }}
            >
                {availablePacks.map((pack) => (
                    <MenuItem
                        key={pack.absPackNo}
                        data-testid={`add-pack-${pack.absPackNo}`}
                        onClick={() => handleSelect(pack)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "16px",
                            padding: "14px 20px",
                            borderBottom: "1px solid #F0F0F0",
                            "&:last-of-type": { borderBottom: 0 },
                            "&:hover": { backgroundColor: "#F7F7FF" },
                        }}
                    >
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1A1A1A" }}>
                            {formatPackChipLabel({
                                stringLabel: pack.stringLabel,
                                packLabel: pack.label,
                            })}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: "#9A9A9A",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {pack.serial || "No serial on record"}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
